import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RefreshCcw, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import Modal from '@/components/modal/Modal.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import TemplateRichTextEditor from '@/features/dashboard/templateImages/components/TemplateRichTextEditor.tsx';
import PlainTextTemplateEditor from '@/components/richText/PlainTextTemplateEditor.tsx';
import useGetTemplateVariables from '@/features/dashboard/messageTemplates/hooks/useGetTemplateVariables.ts';
import useUpdateMessageTemplate from '@/features/dashboard/messageTemplates/hooks/useUpdateMessageTemplate.ts';
import useSendTestEmail from '@/features/dashboard/messageTemplates/hooks/useSendTestEmail.ts';
import useSendTestSms from '@/features/dashboard/messageTemplates/hooks/useSendTestSms.ts';
import messageTemplatesApi from '@/features/dashboard/messageTemplates/api/messageTemplatesApi.ts';
import {
  MessageChannel,
  MessageTemplateDto,
} from '@/features/dashboard/messageTemplates/types/MessageTemplateDto.ts';
import { getMessageTemplateTypeLabel } from '@/features/dashboard/messageTemplates/types/MessageTemplateType.utils.ts';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver';
import {
  createEditEmailTemplateSchema,
  createEditSmsTemplateSchema,
  EditEmailTemplateFormData,
  EditSmsTemplateFormData,
} from '@/features/dashboard/messageTemplates/schemas/editTemplateSchema.ts';
import { getRequiredVariableKeys } from '@/features/dashboard/messageTemplates/types/templateVariables.utils.ts';
import { useInvalidSubmitFeedback } from '@/hooks/useInvalidSubmitFeedback.ts';
import { useConfirmationDialogContext } from '@/components/ui/ConfirmDialog.Context.tsx';

const SUBJECT_VARIABLE_KEYS = new Set([
  'booking.numer',
  'booking.status',
  'booking.bookingDate',
  'tour.name',
  'client.firstName',
  'client.lastName',
]);

const EditTemplateModal = ({
  template,
  onClose,
}: {
  template: MessageTemplateDto;
  onClose: () => void;
}) => {
  const isEmail = template.channel === MessageChannel.EMAIL;
  const [editorKey, setEditorKey] = useState(0);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const { showDialog } = useConfirmationDialogContext();

  const { data: variables } = useGetTemplateVariables(
    template.channel,
    template.messageTemplateType,
  );

  // Keys the backend requires the body to output. The editor mounts only after
  // `variables` resolves (skeleton gate), so these are available before submit.
  const requiredKeys = useMemo(
    () => getRequiredVariableKeys(variables),
    [variables],
  );

  const emailResolver = useMemo(
    () => debugZodResolver(createEditEmailTemplateSchema(requiredKeys)),
    [requiredKeys],
  );
  const smsResolver = useMemo(
    () => debugZodResolver(createEditSmsTemplateSchema(requiredKeys)),
    [requiredKeys],
  );

  const emailForm = useForm<EditEmailTemplateFormData>({
    resolver: emailResolver,
    defaultValues: {
      subjectTemplate: template.subjectTemplate ?? '',
      bodyTemplate: template.bodyTemplate,
    },
  });

  const smsForm = useForm<EditSmsTemplateFormData>({
    resolver: smsResolver,
    defaultValues: {
      bodyTemplate: template.bodyTemplate,
    },
  });

  const form = isEmail ? emailForm : smsForm;

  const formRef = useInvalidSubmitFeedback(form.formState, {
    title: 'Nie można zapisać szablonu',
  });

  // Email subject is a single short line — limit insertable variables to the
  // identifiers most useful in a subject (booking codes, names, dates).
  const subjectVariables = useMemo(
    () => variables?.filter((v) => SUBJECT_VARIABLE_KEYS.has(v.key)),
    [variables],
  );

  const updateTemplate = useUpdateMessageTemplate();
  const sendTestEmail = useSendTestEmail();
  const sendTestSms = useSendTestSms();

  const onSubmit = (
    data: EditEmailTemplateFormData | EditSmsTemplateFormData,
  ) => {
    updateTemplate.mutate(
      {
        ...template,
        subjectTemplate: isEmail
          ? (data as EditEmailTemplateFormData).subjectTemplate
          : null,
        bodyTemplate: data.bodyTemplate,
      },
      { onSuccess: onClose },
    );
  };

  const handleSendTestEmail = async () => {
    const isValid = await emailForm.trigger();
    if (!isValid) return;

    const data = emailForm.getValues();
    sendTestEmail.mutate({
      ...template,
      subjectTemplate: data.subjectTemplate,
      bodyTemplate: data.bodyTemplate,
    });
  };

  const handleSendTestSms = async () => {
    const isValid = await smsForm.trigger();
    if (!isValid) return;

    if (!testPhoneNumber.trim()) {
      toast.error('Podaj numer telefonu do wysyłki testowej');
      return;
    }

    const data = smsForm.getValues();
    sendTestSms.mutate({
      type: template.messageTemplateType,
      bodyTemplate: data.bodyTemplate,
      phoneNumber: testPhoneNumber.trim(),
    });
  };

  const handleRestoreDefaults = async () => {
    const confirmed = await showDialog({
      title: 'Przywróć domyślne',
      description:
        'Czy na pewno chcesz przywrócić domyślny szablon? Niezapisane zmiany zostaną utracone.',
    });
    if (!confirmed) return;

    try {
      const defaultTemplate = await messageTemplatesApi.getDefaultTemplate(
        template.channel,
        template.messageTemplateType,
      );
      if (isEmail) {
        emailForm.reset({
          subjectTemplate: defaultTemplate.subjectTemplate ?? '',
          bodyTemplate: defaultTemplate.bodyTemplate,
        });
      } else {
        smsForm.reset({
          bodyTemplate: defaultTemplate.bodyTemplate,
        });
      }
      setEditorKey((prev) => prev + 1);
    } catch {
      toast.error('Nie udało się pobrać domyślnego szablonu');
    }
  };

  return (
    <Modal onClose={onClose} className="max-w-[800px]">
      <div className="sticky top-0 z-10 flex w-full flex-row items-center justify-between bg-basic-white px-4 py-3">
        <h2 className="text-2xl font-semibold text-base-text">
          {getMessageTemplateTypeLabel(template.messageTemplateType)}
          <span className="ml-2 text-sm font-normal text-neutral-400">
            {isEmail ? 'E-mail' : 'SMS'}
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <ActionButton
            tone="outline"
            size="xs"
            type="button"
            onClick={handleRestoreDefaults}
          >
            <RefreshCcw />
            Przywróć domyślne
          </ActionButton>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-neutral-50"
          >
            <X className="text-neutral-900" size={20} />
          </button>
        </div>
      </div>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit)}>
        <ModalContent>
          <div className="space-y-4">
            {isEmail && (
              <div>
                <InputLabel label="Temat" required={true} />
                <Controller
                  control={emailForm.control}
                  name="subjectTemplate"
                  render={({ field }) => (
                    <PlainTextTemplateEditor
                      key={`subject-${editorKey}`}
                      singleLine
                      content={field.value}
                      variables={subjectVariables}
                      error={
                        emailForm.formState.errors.subjectTemplate?.message
                      }
                      onChange={field.onChange}
                    />
                  )}
                />
                <InputError
                  error={emailForm.formState.errors.subjectTemplate?.message}
                />
              </div>
            )}

            {isEmail ? (
              <div>
                <InputLabel label="Treść" required={true} />
                <Controller
                  control={emailForm.control}
                  name="bodyTemplate"
                  render={({ field }) => (
                    <TemplateRichTextEditor
                      key={editorKey}
                      content={field.value}
                      variables={variables}
                      error={emailForm.formState.errors.bodyTemplate?.message}
                      onChange={field.onChange}
                    />
                  )}
                />
                <InputError
                  error={emailForm.formState.errors.bodyTemplate?.message}
                />
              </div>
            ) : (
              <div>
                <InputLabel label="Treść" required={true} />
                <Controller
                  control={smsForm.control}
                  name="bodyTemplate"
                  render={({ field }) => (
                    <PlainTextTemplateEditor
                      key={editorKey}
                      content={field.value}
                      variables={variables}
                      error={smsForm.formState.errors.bodyTemplate?.message}
                      onChange={field.onChange}
                    />
                  )}
                />
                <InputError
                  error={smsForm.formState.errors.bodyTemplate?.message}
                />
              </div>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          {isEmail ? (
            <ActionButton
              tone="outline"
              size="sm"
              type="button"
              onClick={handleSendTestEmail}
              isLoading={sendTestEmail.isPending}
              className="mr-auto"
            >
              <Send />
              Wyślij testowy e-mail
            </ActionButton>
          ) : (
            <div className="mr-auto flex items-center gap-2">
              <input
                type="tel"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                placeholder="+48 123 456 789"
                className="h-9 w-44 rounded-md border border-neutral-200 px-3 text-sm"
              />
              <ActionButton
                tone="outline"
                size="sm"
                type="button"
                onClick={handleSendTestSms}
                isLoading={sendTestSms.isPending}
              >
                <Send />
                Wyślij testowy SMS
              </ActionButton>
            </div>
          )}
          <ActionButton
            tone="outline"
            size="sm"
            onClick={onClose}
            type="button"
          >
            Zamknij
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={updateTemplate.isPending}
          >
            Zapisz
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditTemplateModal;
